import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { normalizePaginatedListResponse, PaginatedApiResponse, ListQueryOptions } from '../../services/paginatedList';

export type ApprovalStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
export type ApprovalOnApproveAction = 'CONTINUE' | 'COMPLETE';
export type ApprovalOnRejectAction = 'REJECT' | 'RETURN';
export type ApprovalStageMode = 'ALL' | 'ANY';

type BasicUser = { id: number; userDisplayName?: string | null; userFirstName: string; userLastName: string };

export interface ApprovalStep {
  id: number; stage: number; sequence: number; label?: string | null; status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SKIPPED';
  stageMode?: ApprovalStageMode; onApprove?: ApprovalOnApproveAction; onReject?: ApprovalOnRejectAction;
  assigneeUserId?: number | null; assigneeDepartmentId?: number | null; comments?: string | null;
  assigneeUser?: BasicUser | null;
  assigneeDepartment?: { id: number; name?: string; departmentName?: string } | null;
  /** Route to whoever holds this crew rank aboard the approval's vessel. */
  assigneeRankId?: number | null;
  assigneeRank?: { id: number; code?: string | null; name: string } | null;
  /** Hours after stage unlock before this step is considered overdue. */
  slaHours?: number | null;
  escalateUserId?: number | null;
  escalateDepartmentId?: number | null;
  escalateUser?: BasicUser | null;
  escalateDepartment?: { id: number; name?: string; departmentName?: string } | null;
  dueAt?: string | null;
  escalatedAt?: string | null;
  actedByUser?: BasicUser | null;
  actedAt?: string | null;
  /** Present on detail responses — whether the current user may act on this step. */
  canAct?: boolean;
  /** When acting via delegation, the original assignee. */
  actingFor?: BasicUser | null;
}
export interface ApprovalType { id: number; code: string; name: string; kind?: string; isActive?: boolean; }
export interface ApprovalTemplateStep {
  id?: number; stage: number; sequence?: number; label?: string;
  stageMode?: ApprovalStageMode;
  onApprove?: ApprovalOnApproveAction; onReject?: ApprovalOnRejectAction;
  assigneeUserId?: number | null; assigneeDepartmentId?: number | null;
  assigneeRankId?: number | null;
  slaHours?: number | null;
  escalateUserId?: number | null;
  escalateDepartmentId?: number | null;
}
export interface ApprovalTemplate {
  id: number; code: string; name: string; approvalTypeId: number; departmentId?: number | null; contextKey?: string | null; isActive?: boolean;
  /** Inclusive amount band for threshold routing (null/undefined = unbounded). */
  minAmount?: number | string | null;
  maxAmount?: number | string | null;
  approvalType?: ApprovalType; department?: { id: number; name?: string; departmentName?: string } | null;
  steps: ApprovalTemplateStep[];
}
export interface Approval {
  id: number; status: ApprovalStatus; title: string; summary?: string | null; sourceModule: string; sourceEntityType: string; sourceEntityId?: number | null;
  approvalTypeId: number; departmentId?: number | null; contextKey?: string | null; payload?: Record<string, unknown> | null;
  createdAt: string; updatedAt?: string; submittedAt?: string | null; statusAt?: string | null; createdByUserId?: number | null;
  approvalType?: ApprovalType; department?: { id: number; name?: string; departmentName?: string } | null;
  steps?: ApprovalStep[]; createdByUser?: BasicUser | null;
  /** Full trail from payload history, returns, acted steps, created/submitted — present on detail. */
  actionHistory?: Array<{
    action: 'CREATED' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'RETURNED' | 'CANCELLED';
    at: string;
    byUserId: number;
    byUserName?: string | null;
    stepId?: number;
    stage?: number;
    label?: string | null;
    comments?: string | null;
  }>;
  /** Present on My pending inbox responses. */
  inboxRole?: 'actionable' | 'waiting';
  /** Stage the current user is assigned on (My pending). */
  myStage?: number;
  /** When waiting, the earlier stage that must finish first. */
  blockedByStage?: number | null;
}

/// Active "act on behalf of" mapping for approval steps assigned to a user.
export interface ApprovalDelegation {
  id: number;
  userId: number;
  delegateUserId: number;
  validFrom: string;
  validTo?: string | null;
  isActive: boolean;
  note?: string | null;
  createdAt: string;
  user?: BasicUser | null;
  delegateUser?: BasicUser | null;
}

export const useApprovals = (
  inbox: 'mine' | 'submitted' | 'all' = 'mine', page = 1, search = '', options: ListQueryOptions & { status?: string; approvalTypeId?: number; departmentId?: number } = {},
) => {
  const limit = 25;
  const params = new URLSearchParams({ inbox, page: String(page), limit: String(limit) });
  if (search.trim()) params.set('search', search.trim());
  if (options.status) params.set('status', options.status);
  if (options.approvalTypeId) params.set('approvalTypeId', String(options.approvalTypeId));
  if (options.departmentId) params.set('departmentId', String(options.departmentId));
  if (options.sortKey) {
    params.set('sortKey', options.sortKey);
    params.set('sortDirection', options.sortDirection === 'asc' ? 'asc' : 'desc');
  }
  const q = `?${params.toString()}`;
  const result = useSWR<PaginatedApiResponse<Approval> | Approval[]>(`/approvals${q}`, fetcher, {
    revalidateOnFocus: true,
  });
  const normalized = normalizePaginatedListResponse(result.data, page, limit);
  return { data: normalized.data, total: normalized.total, isLoading: result.isLoading, isError: result.error, mutate: result.mutate };
};
export const useApproval = (id?: number) => {
  const result = useSWR<Approval>(id ? `/approvals/${id}` : null, fetcher);
  return { data: result.data, item: result.data, isLoading: result.isLoading, isError: result.error, mutate: result.mutate };
};
export const useApprovalTypes = () => useSWR<ApprovalType[]>('/approval-types', fetcher);
export const useApprovalTemplates = () => useSWR<ApprovalTemplate[]>('/approval-templates', fetcher);
/** Delegations owned by or delegated to the current user (backend scopes to `req.user.id`). */
export const useApprovalDelegations = () => useSWR<ApprovalDelegation[]>('/approvals/delegations', fetcher);
